import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import usePriorities from "@/features/priority/hooks/usePriorities";
import useTaskStates from "@/features/taskState/hooks/useTaskStates";
import { FaFilterCircleXmark } from "react-icons/fa6";
import { TbFilterPlus } from "react-icons/tb";
import useUsers from "@/features/user/hooks/useUsers";
import { Filter, Loader2, X } from "lucide-react";
import { useAuth } from "@/auth/useAuth";
import { isRestrictedToOwnTasks } from "@/features/task/utils/filterUtils";
const computeInitialsFromLabel = (label) => {
    const cleanedLabel = label.trim();
    if (!cleanedLabel) {
        return "??";
    }
    const words = cleanedLabel.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return cleanedLabel.slice(0, 2).toUpperCase();
    }
    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
};
const AssigneeFilter = ({ options, selected, onChange, disabled }) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [placeAbove, setPlaceAbove] = useState(false);
    const [visibleCount, setVisibleCount] = useState(() => selected.length);
    const [containerWidth, setContainerWidth] = useState(0);
    const boxRef = useRef(null);
    const inputRef = useRef(null);
    const chipsContainerRef = useRef(null);
    const optionMap = useMemo(() => new Map(options.map(option => [option.value, option])), [options]);
    const filtered = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        return options.filter(option => {
            if (selected.includes(option.value)) {
                return false;
            }
            if (!normalizedQuery) {
                return true;
            }
            return option.label.toLowerCase().includes(normalizedQuery);
        });
    }, [options, query, selected]);
    useEffect(() => {
        if (!(open || popoverOpen) || !boxRef.current) {
            return;
        }
        const updatePlacement = () => {
            if (!boxRef.current) {
                return;
            }
            const rect = boxRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setPlaceAbove(spaceBelow < 240);
        };
        updatePlacement();
        window.addEventListener("resize", updatePlacement);
        window.addEventListener("scroll", updatePlacement, true);
        return () => {
            window.removeEventListener("resize", updatePlacement);
            window.removeEventListener("scroll", updatePlacement, true);
        };
    }, [open, popoverOpen]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!boxRef.current?.contains(event.target)) {
                setOpen(false);
                setPopoverOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    useEffect(() => {
        if (disabled) {
            setOpen(false);
            setPopoverOpen(false);
        }
    }, [disabled]);
    useEffect(() => {
        if (!boxRef.current || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(entries => {
            entries.forEach(entry => {
                const width = entry.contentRect.width;
                setContainerWidth(previous => {
                    if (Math.abs(previous - width) < 1) {
                        return previous;
                    }
                    return width;
                });
            });
        });
        observer.observe(boxRef.current);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        setVisibleCount(selected.length);
    }, [selected.length, containerWidth]);
    useLayoutEffect(() => {
        const container = chipsContainerRef.current;
        if (!container) {
            return;
        }
        if (container.scrollWidth <= container.clientWidth || visibleCount === 0) {
            return;
        }
        setVisibleCount(previous => (previous > 0 ? previous - 1 : previous));
    }, [visibleCount, selected, containerWidth]);
    const visibleValues = selected.slice(0, visibleCount);
    const hiddenValues = selected.slice(visibleCount);
    useEffect(() => {
        if (hiddenValues.length === 0) {
            setPopoverOpen(false);
        }
    }, [hiddenValues.length]);
    const placementClass = placeAbove ? "bottom-[calc(100%+4px)]" : "top-[calc(100%+4px)]";
    const handleSelect = (value) => {
        if (disabled || selected.includes(value)) {
            return;
        }
        onChange([...selected, value]);
        setQuery("");
        setOpen(true);
        inputRef.current?.focus();
    };
    const handleRemove = (value) => {
        if (disabled) {
            return;
        }
        onChange(selected.filter(selectedValue => selectedValue !== value));
    };
    return (_jsxs("div", { className: "relative w-full min-w-0 text-sm", ref: boxRef, children: [_jsxs("div", { className: "flex w-full items-center gap-2", children: [_jsxs("div", { className: [
                            "flex h-10 min-w-[180px] flex-none items-center rounded-lg border bg-white px-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200",
                            disabled ? "cursor-not-allowed bg-gray-100 opacity-70" : "",
                        ].join(" "), onClick: () => {
                            if (disabled) {
                                return;
                            }
                            inputRef.current?.focus();
                            setOpen(true);
                        }, children: [_jsx("input", { ref: inputRef, value: query, onChange: event => setQuery(event.target.value), onFocus: () => setOpen(true), onKeyDown: event => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        const first = filtered[0];
                                        if (first) {
                                            handleSelect(first.value);
                                        }
                                    }
                                    if (event.key === "Escape") {
                                        setOpen(false);
                                        setPopoverOpen(false);
                                    }
                                }, placeholder: "Assigné", disabled: disabled, className: "w-full border-none bg-transparent py-1 text-sm outline-none placeholder:text-gray-400 focus:ring-0", "aria-expanded": open, "aria-haspopup": "listbox" }), disabled && options.length === 0 && (_jsx(Loader2, { className: "h-4 w-4 animate-spin text-gray-400", "aria-hidden": "true" }))] }), _jsxs("div", { ref: chipsContainerRef, className: "flex h-10 min-w-0 flex-1 items-center gap-1 overflow-hidden", role: "list", "aria-label": "Assign\u00E9s s\u00E9lectionn\u00E9s", children: [visibleValues.map(value => {
                                const option = optionMap.get(value);
                                const label = option?.label ?? `Utilisateur #${value}`;
                                const initials = option?.initials ?? computeInitialsFromLabel(label);
                                return (_jsxs("span", { className: "inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700", children: [_jsx("span", { className: "flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[9px] font-semibold text-white", children: initials }), _jsx("span", { className: "max-w-[140px] truncate", title: label, children: label }), _jsx("button", { type: "button", onClick: event => {
                                                event.stopPropagation();
                                                handleRemove(value);
                                            }, className: "rounded-full p-0.5 text-blue-500 transition hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200", "aria-label": `Retirer ${label}`, children: _jsx(X, { className: "h-3 w-3" }) })] }, value));
                            }), hiddenValues.length > 0 && (_jsx("div", { className: "relative shrink-0", children: _jsxs("button", { type: "button", onClick: event => {
                                        event.stopPropagation();
                                        setPopoverOpen(previous => !previous);
                                    }, onMouseDown: event => event.preventDefault(), className: "rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200", "aria-haspopup": "dialog", "aria-expanded": popoverOpen, children: ["+", hiddenValues.length] }) }))] })] }), open && !disabled && (_jsx("ul", { role: "listbox", className: `absolute left-0 right-0 z-50 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl ${placementClass}`, onMouseDown: event => event.preventDefault(), children: filtered.length === 0 ? (_jsx("li", { className: "px-3 py-2 text-sm text-gray-500", children: "Aucun r\u00E9sultat." })) : (filtered.map(option => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => handleSelect(option.value), className: "flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-blue-50", children: [_jsx("span", { className: "flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600", children: option.initials }), _jsx("span", { className: "truncate", title: option.label, children: option.label })] }) }, option.value)))) })), popoverOpen && hiddenValues.length > 0 && (_jsxs("div", { role: "dialog", "aria-label": "Assign\u00E9s s\u00E9lectionn\u00E9s", className: `absolute right-0 z-50 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-xl ${placementClass}`, onMouseDown: event => event.preventDefault(), children: [_jsx("div", { className: "mb-1 text-xs text-gray-600", children: "Assign\u00E9s s\u00E9lectionn\u00E9s" }), _jsx("ul", { className: "max-h-56 space-y-1 overflow-y-auto", children: hiddenValues.map(value => {
                            const option = optionMap.get(value);
                            const label = option?.label ?? `Utilisateur #${value}`;
                            const initials = option?.initials ?? computeInitialsFromLabel(label);
                            return (_jsxs("li", { className: "flex items-center justify-between gap-2 text-sm", children: [_jsxs("span", { className: "flex items-center gap-2", children: [_jsx("span", { className: "flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[9px] font-semibold text-white", children: initials }), _jsx("span", { className: "truncate", title: label, children: label })] }), _jsx("button", { type: "button", onClick: () => handleRemove(value), className: "rounded px-2 py-0.5 text-xs text-blue-600 transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200", children: "Retirer" })] }, value));
                        }) })] }))] }));
};
const TaskTableFilters = ({ filters, isKanban, onFilterChange, onResetFilters }) => {
    const { priorities } = usePriorities();
    const { taskStates } = useTaskStates();
    const { users, loading } = useUsers(undefined, undefined, true);
    const { user } = useAuth();
    const isRestrictedUser = isRestrictedToOwnTasks(user);
    const [moreFilters, setMoreFilters] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const assigneeOptions = useMemo(() => {
        return users.map(user => {
            const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
            const label = fullName || user.email || `Utilisateur #${user.id}`;
            const rawInitials = `${user.first_name?.charAt(0) ?? ""}${user.last_name?.charAt(0) ?? ""}`.toUpperCase();
            const initials = rawInitials || computeInitialsFromLabel(label);
            return {
                value: user.id.toString(),
                label,
                initials,
            };
        });
    }, [users]);
    const selectedAssignees = Array.isArray(filters.assignees) ? filters.assignees : [];
    const assigneeFilterDisabled = loading || isRestrictedUser;
    const filterFieldClassName = "h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200";
    const handleChange = (e) => {
        onFilterChange(e.target.name, e.target.value);
    };
    const handleAssigneesFilterChange = (values) => {
        onFilterChange("assignees", values);
    };
    const handleMoreFilters = () => {
        setMoreFilters(!moreFilters);
    };
    const activeFiltersCount = Object.values(filters).filter(value => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== "" && value !== undefined && value !== null;
    }).length;
    return (_jsxs("div", { className: "relative z-40 overflow-visible", children: [_jsxs("div", { className: "mb-4 flex items-center gap-2 sm:justify-between", children: [_jsxs("button", { onClick: () => setIsOpen(!isOpen), className: "flex items-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 transition-colors hover:bg-gray-50", children: [_jsx(Filter, { className: "h-4 w-4" }), _jsx("span", { children: "Filtres" }), activeFiltersCount > 0 && (_jsx("span", { className: "rounded-full bg-blue-500 px-2 py-1 text-xs text-white", children: activeFiltersCount }))] }), _jsxs("div", { className: `${(isOpen || activeFiltersCount > 0) ? "max-h-40 opacity-100 overflow-visible" : "max-h-0 opacity-0 overflow-hidden"} flex gap-2 justify-end transition-[max-height,opacity] duration-300`, children: [_jsxs("button", { onClick: onResetFilters, title: "R\u00E9initialiser les filtres", "aria-label": "R\u00E9initialiser les filtres", className: `${activeFiltersCount ? "bg-gray-500 hover:bg-gray-600" : "bg-gray-300"} w-full rounded px-4 py-2 text-white sm:w-auto`, children: [_jsx(FaFilterCircleXmark, { "aria-hidden": "true" }), _jsx("span", { className: "sr-only", children: "R\u00E9initialiser les filtres" })] }), _jsxs("button", { onClick: handleMoreFilters, title: moreFilters ? "Masquer les filtres" : "Afficher plus de filtres", "aria-label": moreFilters ? "Masquer les filtres" : "Afficher plus de filtres", className: `${moreFilters ? "bg-green-100 hover:bg-green-400" : "bg-green-400 hover:bg-green-100"} w-full rounded px-4 py-2 text-white sm:w-auto`, children: [_jsx(TbFilterPlus, { "aria-hidden": "true" }), _jsx("span", { className: "sr-only", children: moreFilters ? "Masquer les filtres" : "Afficher plus de filtres" })] })] })] }), _jsx("div", { className: `${isOpen ? "max-h-[1200px] opacity-100 overflow-visible" : "max-h-0 opacity-0 overflow-hidden"} transition-[max-height,opacity] duration-300`, children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "grid grid-cols-1 items-start gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7", children: [_jsx("input", { type: "text", name: "search", value: filters.search || "", onChange: handleChange, placeholder: "Rechercher...", className: filterFieldClassName }), _jsx("input", { type: "text", name: "ref_fonciere", value: filters.ref_fonciere || "", onChange: handleChange, placeholder: "R\u00E9f. fonci\u00E8re", className: filterFieldClassName }), !isKanban && (_jsxs("select", { name: "status", value: filters.status || "", onChange: handleChange, className: `${filterFieldClassName} pr-8`, children: [_jsx("option", { value: "", children: "Tous les statuts" }), taskStates.map(state => (_jsx("option", { value: state.code, children: state.label }, state.id)))] })), _jsxs("select", { name: "priority", value: filters.priority || "", onChange: handleChange, className: `${filterFieldClassName} pr-8`, children: [_jsx("option", { value: "", children: "Toutes les priorit\u00E9s" }), priorities.map(priority => (_jsx("option", { value: priority.code, children: priority.label }, priority.code)))] }), _jsx("div", { className: "md:col-span-2 lg:col-span-3", children: _jsx(AssigneeFilter, { options: assigneeOptions, selected: selectedAssignees, onChange: handleAssigneesFilterChange, disabled: assigneeFilterDisabled }) })] }), moreFilters && (_jsxs("div", { className: "grid grid-cols-1 items-start gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5", children: [_jsx("input", { type: "text", name: "city", value: filters.city || "", onChange: handleChange, placeholder: "Rechercher une ville...", className: filterFieldClassName }), _jsx("input", { type: "text", name: "client", value: filters.client || "", onChange: handleChange, placeholder: "Rechercher un client...", className: filterFieldClassName }), _jsx("input", { type: "text", name: "project", value: filters.project || "", onChange: handleChange, placeholder: "Rechercher un projet...", className: filterFieldClassName })] }))] }) })] }));
};
export default TaskTableFilters;
